import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Mail, Calendar, CreditCard, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PasswordDialog } from "@/components/admin/users/PasswordDialog";

export default function AdminUserDetail() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  type Profile = {
    id: string;
    email?: string | null;
    name?: string | null;
    avatar_url?: string | null;
    created_at?: string | null;
    subscription_tier?: string | null;
    subscription_status?: string | null;
  };
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  useEffect(() => {
    async function fetchUser() {
      if (!id) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id)
        .single();
        
      if (data) {
        setUser(data);
      }
      setLoading(false);
    }
    fetchUser();
  }, [id]);

  if (loading) return <div>{t('common.loading')}</div>;
  if (!user) return <div>User not found</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <button 
          onClick={() => navigate("/admin/users")}
          className="rounded-xl border-2 border-transparent p-2 transition-all hover:border-black hover:bg-white hover:shadow-[4px_4px_0_#000]"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div>
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.userDetail.title')}</h2>
          <p className="text-lg font-medium text-gray-500">{t('admin.userDetail.subtitle')}</p>
        </div>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        {/* Profile Card */}
        <div className="h-full rounded-2xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000]">
          <div className="flex items-center gap-6 border-b-2 border-black/5 pb-8">
            <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-black bg-[#FFD93D] text-3xl font-black">
               {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
               ) : (
                  (user.name || "U").charAt(0).toUpperCase()
               )}
            </div>
            <div>
              <h3 className="text-2xl font-black text-[#1A1A1A]">{user.name || t('admin.userDetail.unnamedUser')}</h3>
              <p className="font-medium text-gray-500">{user.email}</p>
            </div>
          </div>
          
          <div className="mt-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 border-2 border-transparent">
                <Calendar className="h-5 w-5 text-gray-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-black/60">{t('admin.userDetail.registeredAt')}</p>
                <p className="font-bold text-[#1A1A1A]">{new Date(user.created_at).toLocaleString()}</p>
              </div>
            </div>
             <div className="flex items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 border-2 border-transparent">
                <CreditCard className="h-5 w-5 text-gray-700" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-wider text-black/60">{t('admin.userDetail.subscriptionPlan')}</p>
                <div className="flex items-center gap-2">
                  <p className="font-bold capitalize text-[#1A1A1A]">{user.subscription_tier || "Free"}</p>
                  {user.subscription_status === 'gifted' && (
                    <span className="rounded-full border-2 border-black bg-[#E0B0FF] px-2 py-0.5 text-[10px] font-bold">
                      {t('admin.userDetail.giftedByAdmin')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions / Stats */}
        <div className="h-full rounded-2xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000] flex flex-col justify-between">
           <div>
             <h3 className="mb-6 flex items-center gap-3 text-xl font-black text-[#1A1A1A]">
               <Shield className="h-6 w-6" />
               {t('admin.userDetail.accountStatus')}
             </h3>
             <div className="flex flex-col gap-4">
               <div className="grid gap-4 sm:grid-cols-2">
                 <button 
                  onClick={() => setPasswordDialogOpen(true)}
                  className="rounded-xl border-2 border-black bg-[#6FEDD6] px-4 py-3 font-black shadow-[4px_4px_0_#000] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
                 >
                   {t('admin.userDetail.resetPassword')}
                 </button>
                 
                 <button 
                  onClick={async () => {
                      const isSuspended = user.subscription_status === 'suspended';
                      const newStatus = isSuspended ? 'active' : 'suspended';
                      const confirmMsg = isSuspended 
                          ? t('admin.userDetail.confirmEnable', { name: user.name })
                          : t('admin.userDetail.confirmDisable', { name: user.name });
                      
                      if (!confirm(confirmMsg)) return;
                      
                      const { error } = await supabase.from("profiles").update({ subscription_status: newStatus }).eq("id", user.id);
                      if (error) {
                          toast({ 
                            variant: "destructive",
                            title: t('admin.userDetail.updateFailed') 
                          });
                      } else {
                          setUser({ ...user, subscription_status: newStatus });
                          toast({ 
                            title: isSuspended ? t('admin.userDetail.accountEnabled') : t('admin.userDetail.accountDisabled') 
                          });
                      }
                  }}
                  className={`rounded-xl border-2 border-black px-4 py-3 font-black text-white shadow-[4px_4px_0_#000] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_#000] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none ${
                      user.subscription_status === 'suspended' ? 'bg-gray-500' : 'bg-[#FF6B9D]'
                  }`}
                 >
                   {user.subscription_status === 'suspended' ? t('admin.userDetail.enableAccount') : t('admin.userDetail.disableAccount')}
                 </button>
               </div>
               
               <div className="rounded-xl border-2 border-black/10 bg-gray-50 p-4">
                 <div className="flex items-center gap-2">
                   <div className={`h-2 w-2 rounded-full ${user.subscription_status === 'suspended' ? 'bg-red-500' : 'bg-green-500'}`} />
                   <span className="font-bold text-gray-600">
                     {t('admin.userDetail.currentStatus')} {
                        user.subscription_status === 'suspended' ? t('admin.userDetail.status.suspended') : 
                        (user.subscription_status === 'gifted' ? t('admin.userDetail.status.gifted') : 
                        (user.subscription_status === 'trialing' ? t('admin.userDetail.status.trialing') : t('admin.userDetail.status.active')))
                     }
                   </span>
                 </div>
               </div>
             </div>
           </div>
        </div>
      </div>

      <PasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={setPasswordDialogOpen}
        userId={user.id}
        userName={user.name}
      />
    </div>
  );
}
